import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Section,
  Container,
  SideMenu,
  Main,
  Aside,
  FooterNav,
  DivLang,
  MobileSupportTab,
  ShellBackground,
  MainArticle,
  RoutedContent,
  ChatPanelAnnouncement,
} from "./styles";
import { Company, db, LoggedUser, UserConfigs } from "../../db/db";
import CsatSurveyManager from "../../components/CsatSurveyManager";
import NavBar from "../../components/UIelements/NavBar";
import RotateBanner from "../../components/UIelements/RotateBanner";
import FooterBar from "./../../components/UIelements/FooterBar/index";
import SelectLang from "../../components/UIelements/SelectLang";
import Loading from "../../components/Loading";
import imageLogin1 from "../../assets/image/backgrounds/imageLogin1.jpg";
import imageLogin2 from "../../assets/image/backgrounds/imageLogin2.jpg";
import imageLogin3 from "../../assets/image/backgrounds/imageLogin3.jpg";
import PrivacyBanner from "../../components/UIelements/PrivacyBanner";
import {
  isZendeskMessagingEnabled,
  ZendeskUnreadState,
} from "../../integrations/zendeskMessaging";
import { CHAT_MOBILE_DIALOG_ID } from "../Chat/constants";
import { useZendeskMessaging } from "../../integrations/ZendeskMessagingProvider";
import ChatUnreadBadge, {
  ChatUnreadLiveRegion,
  formatChatControlLabel,
} from "../../components/UIelements/ChatUnreadBadge";
import { getAuthenticatedHomePath } from "../../routes/ChatRoute";
import {
  CHAT_ROUTE_ENTRY_STATE,
  ChatOutletContextValue,
} from "./chatOutletContext";
import { HeaderProvider } from "../../context/HeaderContext";
import { Header } from "./_components/Header";
import { useScreen } from "../../context/ScreenContext";
import PersistentChatSurface from "../Chat/PersistentChatSurface";
import {
  ChatDesktopMode,
  INITIAL_CHAT_OFF_ROUTE_MODE,
  reduceChatPresentation,
} from "../Chat/chatPresentation";
import ZendeskMessagingThemeSync from "../../integrations/ZendeskMessagingThemeSync";
import PwaInstallPrompt from "../../components/PwaInstallPrompt";
import { useMobileChatOverlay } from "./useMobileChatOverlay";
import { lockMobileChatBackground } from "./lockMobileChatBackground";
import { emitChatMobileNavigationTelemetry } from "../../integrations/chatMobileNavigationTelemetry";
import { emitChatPanelTelemetry } from "../../integrations/chatPanelTelemetry";
import {
  CHAT_DEFAULT_ROOT_FONT_SIZE_PX,
  getChatPanelPinMinViewportWidth,
} from "../Chat/constants";

interface Props {
  user?: LoggedUser[];
  configs?: UserConfigs[];
  company?: Company[];
}

const UNKNOWN_UNREAD: ZendeskUnreadState = { status: "unknown", count: null };

type AutoCollapsedSidebarState = {
  active: boolean;
  previousCollapsed: boolean;
  userChanged: boolean;
};

const INITIAL_AUTO_COLLAPSED_SIDEBAR: AutoCollapsedSidebarState = {
  active: false,
  previousCollapsed: false,
  userChanged: false,
};

const Layout = ({ user, configs, company }: Props) => {
  const intl = useIntl();
  const {
    unread,
    deactivateEmbedded,
    recordSupportEntryPoint,
    reportChatOpenedFromUnread
  } = useZendeskMessaging();
  const visibleUnread = isZendeskMessagingEnabled ? unread : UNKNOWN_UNREAD;
  const [showNavbar, setShowNavbar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop
  const [chatPanelPinned, setChatPanelPinned] = useState(false);
  const autoCollapsedSidebarRef = useRef<AutoCollapsedSidebarState>({
    ...INITIAL_AUTO_COLLAPSED_SIDEBAR,
  });
  const previousPinnedLayoutRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { isMobile, screenSize } = useScreen();
  const chatRouteMatch = useMatch("/chat");
  const shellTopRef = useRef<HTMLDivElement>(null);
  const shellBottomRef = useRef<HTMLDivElement>(null);
  const desktopChatTriggerRef = useRef<HTMLAnchorElement>(null);
  const mobileChatTriggerRef = useRef<HTMLElement | null>(null);
  const headerBackgroundRef = useRef<HTMLDivElement>(null);
  const routedContentRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [unmadeTutorials, setUnmadeTutorials] = useState<any>();
  const [modalRecoverAccount, setModalRecoverAccount] =
    useState<boolean>(false);
  const [alreadyShown, setAlreadyShown] = useState<boolean>(false);
  const id: any = configs ? (configs[0]?.id ?? 1) : 1;
  const maintenance: Boolean = false;
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [chatPageActive, setChatPageActive] = useState(false);
  const [offRouteChatMode, dispatchChatPresentation] = useReducer(
    reduceChatPresentation,
    INITIAL_CHAT_OFF_ROUTE_MODE
  );
  const [conversationListCollapsed, setConversationListCollapsed] = useState(false);
  const [chatPanelAnnouncement, setChatPanelAnnouncement] = useState('');
  const isAuthenticatedLayout = Boolean(
    user?.[0] &&
    configs?.length &&
    ![
      "/resetPassword",
      "/activate/recover/user",
      "/activate",
      "/recoverAccount",
      "/entity",
      "/companyMerge",
      "/register",
      "/recuse/user",
      "/resetDeveloperToken",
    ].includes(pathname),
  );
  const {
    isOpen: isMobileOverlayOpen,
    view: mobileChatView,
    open: openMobileOverlay,
    close: closeMobileOverlay,
    showConversation: showMobileConversation,
    showConversationList: showMobileConversationList
  } = useMobileChatOverlay(
    isZendeskMessagingEnabled && isAuthenticatedLayout && isMobile && !chatRouteMatch,
    location.key
  );
  const mobileOverlayVisible = isMobile && isMobileOverlayOpen;
  const previousMobileChatViewRef = useRef(mobileChatView);
  const authenticatedHomePath = getAuthenticatedHomePath(company?.[0]?.companyType);
  const lastAuthenticatedRouteRef = useRef<string | null>(null);
  const canOpenDesktopPanel = !isMobile;
  const desktopChatMode: ChatDesktopMode = chatPageActive
    ? 'page'
    : offRouteChatMode;
  const effectiveDesktopChatMode: ChatDesktopMode = isMobile
    ? 'closed'
    : desktopChatMode;
  const computedRootFontSize = typeof window === 'undefined'
    ? CHAT_DEFAULT_ROOT_FONT_SIZE_PX
    : Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  const rootFontSize = Number.isFinite(computedRootFontSize)
    ? computedRootFontSize
    : CHAT_DEFAULT_ROOT_FONT_SIZE_PX;
  const chatPanelPinAvailable = !isMobile
    && screenSize.width >= getChatPanelPinMinViewportWidth(rootFontSize);
  const effectiveChatPanelPinned = effectiveDesktopChatMode === 'panel'
    && chatPanelPinned
    && chatPanelPinAvailable;
  const chatPresentationActiveRef = useRef(false);
  // The route match also covers the handoff before Chat registers its page state.
  chatPresentationActiveRef.current = Boolean(chatRouteMatch) || (isMobile
    ? chatPageActive || isMobileOverlayOpen
    : ['page', 'panel'].includes(effectiveDesktopChatMode));

  useEffect(() => {
    if (!isAuthenticatedLayout || chatRouteMatch) return;
    lastAuthenticatedRouteRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [chatRouteMatch, isAuthenticatedLayout, location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (chatPageActive || offRouteChatMode !== 'panel' || canOpenDesktopPanel) return;
    dispatchChatPresentation({ type: 'minimize' });
    setChatPanelAnnouncement(
      intl.formatMessage({ id: 'chat.panelMinimizedForWidth' })
    );
  }, [canOpenDesktopPanel, chatPageActive, intl, offRouteChatMode]);

  const getUserConfigs = async () => {
    if (configs && (configs[0] === undefined || configs.length === 0)) {
      const existingUserConfigs = await db.userConfigs.get(1);
      if (!existingUserConfigs) {
        await db.userConfigs.add({
          id: 1,
          userId: 1,
          language: "PT",
          tempUnit: "°C",
          weightUnit: "Gramas",
          theme: "Light",
          showAgain: true,
          recover: true,
          tutorialsUnmade: unmadeTutorials,
          showBanner: true,
        });
      }
    } else if (configs && configs[0].id && !("showBanner" in configs[0])) {
      await db.userConfigs.update(configs[0].id, { showBanner: true });
    }
  };

  useEffect(() => {
    getUserConfigs();
  }, [configs]);

  useEffect(() => {
    if (configs && !alreadyShown) {
      setModalRecoverAccount(configs[0]?.showAgain ?? false);
      setAlreadyShown(true);
      setShowBanner(configs[0]?.showBanner ?? false);
    }
  }, [configs, id]);

  useEffect(() => {
    if (user && user[0] && configs && company && company[0]) {
      if (user[0].accessToken && window.location.pathname === "/login") {
        switch (company[0].companyType ? company[0].companyType : "") {
          case "Analytics":
            navigate("/analytics");
            break;
          case "Suporte":
            navigate("/equipment");
            break;
          case "Master":
            navigate("/home/master");
            break;
          default:
            navigate("/home");
            break;
        }
      }
    } else if (window.location.pathname === "/login") {
      navigate("/login");
    } else if (window.location.pathname === "/register") {
      navigate("/register");
    } else if (window.location.pathname === "/recoverPassword") {
      navigate("/recoverPassword");
    } else if (window.location.pathname === "/resetPassword") {
    } else if (window.location.pathname === "/resetDeveloperToken") {
    } else if (window.location.pathname === "/activate") {
    } else if (window.location.pathname === "/activate/recover/user") {
    } else if (window.location.pathname === "/recuse/user") {
    } else if (window.location.pathname === "/recoverAccount") {
    } else if (window.location.pathname === "/companyMerge") {
    } else if (window.location.pathname === "/requestAccess") {
      navigate("/requestAccess");
    } else if (window.location.pathname === "/recoverAccount") {
      navigate("/recoverAccount");
    } else if (window.location.pathname === "/entity") {
    } else if (user && user[0] && window.location.pathname === "/menu") {
      navigate("/menu");
    } else if (user && user[0] && window.location.pathname === "/equipment") {
      navigate("/equipment");
    } else if (window.location.pathname === "/") {
      if (maintenance === true) {
        navigate("/maintenance");
      } else {
        navigate("/login");
      }
    } else {
      navigate(window.location.pathname);
    }
  }, [user, configs, company]);

  const handleToggleNavbar = useCallback(
    () => setShowNavbar((prevState) => !prevState),
    [],
  );

  const handleHideNavbar = useCallback(() => setShowNavbar(false), []);

  const restoreAutoCollapsedSidebar = useCallback((preserveUserOverride = false) => {
    const autoCollapsed = autoCollapsedSidebarRef.current;
    if (autoCollapsed.active && !autoCollapsed.userChanged) {
      setSidebarCollapsed(autoCollapsed.previousCollapsed);
    }
    if (preserveUserOverride && autoCollapsed.userChanged) return;
    autoCollapsedSidebarRef.current = { ...INITIAL_AUTO_COLLAPSED_SIDEBAR };
  }, []);

  const handleToggleSidebarCollapsed = useCallback(() => {
    if (chatPanelPinned) {
      autoCollapsedSidebarRef.current.userChanged = true;
    }
    setSidebarCollapsed((current) => !current);
  }, [chatPanelPinned]);

  useLayoutEffect(() => {
    const wasPinned = previousPinnedLayoutRef.current;
    previousPinnedLayoutRef.current = effectiveChatPanelPinned;

    if (!wasPinned && effectiveChatPanelPinned) {
      if (autoCollapsedSidebarRef.current.userChanged) return;
      if (!sidebarCollapsed) {
        autoCollapsedSidebarRef.current = {
          active: true,
          previousCollapsed: false,
          userChanged: false,
        };
        setSidebarCollapsed(true);
      } else {
        autoCollapsedSidebarRef.current = { ...INITIAL_AUTO_COLLAPSED_SIDEBAR };
      }
      return;
    }

    if (wasPinned && !effectiveChatPanelPinned) {
      restoreAutoCollapsedSidebar(chatPanelPinned);
      return;
    }

    if (!effectiveChatPanelPinned && !chatPanelPinned) {
      autoCollapsedSidebarRef.current = { ...INITIAL_AUTO_COLLAPSED_SIDEBAR };
    }
  }, [
    chatPanelPinned,
    effectiveChatPanelPinned,
    restoreAutoCollapsedSidebar,
    sidebarCollapsed
  ]);

  useEffect(() => {
    if (
      desktopChatMode !== 'panel'
      || !chatPanelPinned
      || chatPanelPinAvailable
    ) return;
    setChatPanelPinned(false);
    emitChatPanelTelemetry({
      name: 'chat_panel_unpinned',
      reason: 'insufficient_width',
    });
  }, [chatPanelPinAvailable, chatPanelPinned, desktopChatMode]);

  const toggleChatPanelPinned = useCallback(() => {
    if (chatPanelPinned) {
      setChatPanelPinned(false);
      emitChatPanelTelemetry({ name: 'chat_panel_unpinned', reason: 'user' });
      return;
    }

    if (!chatPanelPinAvailable) {
      emitChatPanelTelemetry({
        name: 'chat_panel_layout_failed',
        reason: 'insufficient_width',
      });
      return;
    }

    setChatPanelPinned(true);
    emitChatPanelTelemetry({ name: 'chat_panel_pinned' });
  }, [chatPanelPinAvailable, chatPanelPinned]);

  const handleOpenMobileChat = useCallback(
    (trigger?: HTMLElement | null) => {
      if (!isZendeskMessagingEnabled || !isMobile || chatRouteMatch) return;
      mobileChatTriggerRef.current = trigger ?? (document.activeElement as HTMLElement | null);
      if (!openMobileOverlay()) return;
      recordSupportEntryPoint('mobile_tab');
      reportChatOpenedFromUnread('mobile_tab');
    },
    [chatRouteMatch, isMobile, openMobileOverlay, recordSupportEntryPoint, reportChatOpenedFromUnread],
  );

  const handleCloseMobileChat = useCallback(() => {
    if (!chatRouteMatch) {
      closeMobileOverlay();
      return;
    }
    const state = (location.state ?? {}) as Record<string, unknown>;
    if (state[CHAT_ROUTE_ENTRY_STATE]) {
      navigate(-1);
      return;
    }
    navigate(authenticatedHomePath, { replace: true });
  }, [authenticatedHomePath, chatRouteMatch, location.state, closeMobileOverlay, navigate]);

  const activateMobileChat = useCallback(() => lockMobileChatBackground(
    [shellTopRef.current, shellBottomRef.current, headerBackgroundRef.current, routedContentRef.current]
      .filter((element): element is HTMLDivElement => Boolean(element)),
    sectionRef.current,
    mobileChatTriggerRef.current
  ), []);

  useLayoutEffect(() => {
    if (!mobileOverlayVisible) return;
    return activateMobileChat();
  }, [activateMobileChat, mobileOverlayVisible]);

  useEffect(() => {
    if (!mobileOverlayVisible) return;
    return () => {
      // Let the persistent surface observe mobileOpen=false before SDK close
      // callbacks fire. Preserve any route, panel or newer overlay taking over.
      Promise.resolve().then(() => {
        if (!chatPresentationActiveRef.current) deactivateEmbedded();
      });
    };
  }, [deactivateEmbedded, mobileOverlayVisible]);

  useEffect(() => {
    const previousView = previousMobileChatViewRef.current;
    previousMobileChatViewRef.current = mobileChatView;
    if (previousView === mobileChatView) return;
    emitChatMobileNavigationTelemetry({
      name: mobileChatView === 'conversationList'
        ? 'chat_mobile_conversation_list_opened'
        : 'chat_mobile_conversation_list_closed'
    });
  }, [mobileChatView]);

  const handleMobileConversationSelected = useCallback(() => {
    emitChatMobileNavigationTelemetry({
      name: 'chat_mobile_conversation_selected'
    });
    showMobileConversation();
  }, [showMobileConversation]);

  const activateChatPage = useCallback(() => {
    setChatPageActive(true);
    return () => setChatPageActive(false);
  }, []);

  const toggleConversationList = useCallback(() => {
    setConversationListCollapsed((collapsed) => !collapsed);
  }, []);

  const openChatPanelFromPage = useCallback(() => {
    if (!canOpenDesktopPanel) return;
    recordSupportEntryPoint('panel');
    dispatchChatPresentation({ type: 'open-panel' });
    navigate(lastAuthenticatedRouteRef.current || authenticatedHomePath);
  }, [authenticatedHomePath, canOpenDesktopPanel, navigate, recordSupportEntryPoint]);

  const minimizeChatPanel = useCallback(() => {
    setChatPanelAnnouncement('');
    dispatchChatPresentation({ type: 'minimize' });
  }, []);

  const restoreChatPanel = useCallback(() => {
    setChatPanelAnnouncement('');
    recordSupportEntryPoint('minimized_control');
    reportChatOpenedFromUnread('panel_minimized');
    if (!canOpenDesktopPanel) {
      navigate('/chat');
      return;
    }
    dispatchChatPresentation({ type: 'restore' });
  }, [canOpenDesktopPanel, navigate, recordSupportEntryPoint, reportChatOpenedFromUnread]);

  const closeChatPanel = useCallback(() => {
    setChatPanelAnnouncement('');
    dispatchChatPresentation({ type: 'close' });
    const focusTrigger = () => desktopChatTriggerRef.current?.focus();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(focusTrigger);
    } else {
      window.setTimeout(focusTrigger, 0);
    }
  }, []);

  const openFullChatPage = useCallback(() => {
    recordSupportEntryPoint('chat_page');
    navigate('/chat');
  }, [navigate, recordSupportEntryPoint]);

  const reportDesktopChatNavigation = useCallback(() => {
    recordSupportEntryPoint('desktop_nav');
    reportChatOpenedFromUnread('desktop_nav');
  }, [recordSupportEntryPoint, reportChatOpenedFromUnread]);

  const chatOutletContext = useMemo<ChatOutletContextValue>(() => ({
    activateMobileChat,
    activateChatPage,
    authenticatedHomePath,
    canOpenDesktopPanel,
    conversationListCollapsed,
    openChatPanelFromPage,
    toggleConversationList
  }), [
    activateChatPage,
    activateMobileChat,
    authenticatedHomePath,
    canOpenDesktopPanel,
    conversationListCollapsed,
    openChatPanelFromPage,
    toggleConversationList
  ]);

  const handleOpenSupportChat = useCallback(() => {
    if (isZendeskMessagingEnabled) return;

    const zendesk = (window as any).zE;

    if (typeof zendesk !== "function") return;

    document.body.classList.add("mobile-zendesk-open");

    try {
      zendesk("messenger", "show");
      zendesk("messenger", "open");
      return;
    } catch {
      try {
        zendesk("webWidget", "show");
        zendesk("webWidget", "open");
      } catch {
        document.body.classList.remove("mobile-zendesk-open");
      }
    }
  }, []);

  useEffect(() => {
    const handleBackButtonEvent = (e: any) => {
      e.preventDefault();
      if (window.location.pathname === "/login") {
        db.user.clear();
        db.userConfigs.clear();
        db.company.clear();
        setTimeout(function () {
          navigate("/login");
          window.location.reload();
        }, 50);
      }
    };
    window.addEventListener("popstate", handleBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", handleBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.getElementById("header");
      if (window.pageYOffset > 100) {
        if (header) header.style.display = "none";
      } else {
        if (header) header.style.display = "block";
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isZendeskMessagingEnabled || !isAuthenticatedLayout) {
      document.body.classList.remove("mobile-zendesk-tab");
      document.body.classList.remove("mobile-zendesk-open");
      return;
    }

    document.body.classList.add("mobile-zendesk-tab");

    const handleZendeskOpen = () => {
      document.body.classList.add("mobile-zendesk-open");
    };

    const handleZendeskClose = () => {
      document.body.classList.remove("mobile-zendesk-open");
    };

    const zendesk = (window as any).zE;

    if (typeof zendesk === "function") {
      try {
        zendesk("messenger:on", "open", handleZendeskOpen);
        zendesk("messenger:on", "close", handleZendeskClose);
      } catch {
        // Widget pode nao expor esta API.
      }

      try {
        zendesk("webWidget:on", "open", handleZendeskOpen);
        zendesk("webWidget:on", "close", handleZendeskClose);
      } catch {
        // Widget pode nao expor esta API.
      }
    }

    return () => {
      document.body.classList.remove("mobile-zendesk-tab");
      document.body.classList.remove("mobile-zendesk-open");
    };
  }, [isAuthenticatedLayout]);
  if (!user && window.location.pathname !== "/resetPassword")
    return <Loading />;

  return (
    <HeaderProvider>
      <ZendeskMessagingThemeSync
        hideConversationListHeader={!isMobile && Boolean(chatRouteMatch)}
        isChatPage={!isMobile && Boolean(chatRouteMatch)}
      />
      {isAuthenticatedLayout ? (
        <Container
          $chatPinned={isZendeskMessagingEnabled && effectiveChatPanelPinned}
          $sidebarCollapsed={sidebarCollapsed}
        >
          <ShellBackground ref={shellTopRef}>
            <SideMenu className={showNavbar ? "active" : ""}>
              <NavBar
                variant={isMobile ? "mobile" : "web"}
                collapsed={sidebarCollapsed}
                onToggleCollapsed={handleToggleSidebarCollapsed}
                handleHideNavbar={handleHideNavbar}
                companyId={user![0].companyId}
                companyType={company?.[0]?.companyType}
                theme={configs?.[0].theme ?? "Light"}
                chatAvailable={
                  !["panel", "minimized"].includes(effectiveDesktopChatMode)
                }
                chatUnread={visibleUnread}
                chatActionRef={desktopChatTriggerRef}
                onChatOpenedFromUnread={reportDesktopChatNavigation}
              />
            </SideMenu>
          </ShellBackground>
          <MainArticle id="main-article">
            <ShellBackground ref={headerBackgroundRef}><Header /></ShellBackground>
            <Section
              ref={sectionRef}
              className={pathname === "/home" ? "home-page-section" : undefined}
              $chatMode={effectiveDesktopChatMode}
            >
              <RoutedContent ref={routedContentRef} $chatMode={effectiveDesktopChatMode}>
                <Outlet context={chatOutletContext} />
              </RoutedContent>
              {isZendeskMessagingEnabled && (
                <PersistentChatSurface
                  isMobile={isMobile}
                  desktopMode={effectiveDesktopChatMode}
                  mobileOpen={isMobile && (chatPageActive || isMobileOverlayOpen)}
                  mobileView={mobileChatView}
                  panelPinned={effectiveChatPanelPinned}
                  pinAvailable={chatPanelPinAvailable}
                  conversationListCollapsed={conversationListCollapsed}
                  unread={visibleUnread}
                  onClosePanel={closeChatPanel}
                  onMinimizePanel={minimizeChatPanel}
                  onOpenFullPage={openFullChatPage}
                  onTogglePanelPinned={toggleChatPanelPinned}
                  onOpenMobileConversationList={showMobileConversationList}
                  onRequestClose={handleCloseMobileChat}
                  onRestorePanel={restoreChatPanel}
                  onShowMobileConversation={showMobileConversation}
                  onMobileConversationSelected={handleMobileConversationSelected}
                  onToggleConversationList={toggleConversationList}
                />
              )}
            </Section>
          </MainArticle>
          <ShellBackground ref={shellBottomRef}>
            <CsatSurveyManager user={user} />
            <FooterNav>
              <FooterBar
                handleToggleNavbar={handleToggleNavbar}
                handleHideNavbar={handleHideNavbar}
                navbarIsVisible={showNavbar}
              />
            </FooterNav>
            <MobileSupportTab
              type="button"
              data-testid="mobile-support-tab"
              data-chat-source="mobile_tab"
              aria-label={formatChatControlLabel(intl, visibleUnread)}
              aria-controls={
                isZendeskMessagingEnabled ? CHAT_MOBILE_DIALOG_ID : undefined
              }
              aria-expanded={
                isZendeskMessagingEnabled ? (Boolean(chatRouteMatch) || mobileOverlayVisible) : undefined
              }
              onClick={(event) => {
                if (isZendeskMessagingEnabled) {
                  handleOpenMobileChat(event.currentTarget);
                  return;
                }
                handleOpenSupportChat();
              }}
            >
              <span>{intl.formatMessage({ id: "home.Chat" })}</span>
              <ChatUnreadBadge unread={visibleUnread} />
            </MobileSupportTab>
          </ShellBackground>
          <ChatUnreadLiveRegion unread={visibleUnread} />
          {chatPanelAnnouncement && (
            <ChatPanelAnnouncement aria-live="polite" aria-atomic="true">
              {chatPanelAnnouncement}
            </ChatPanelAnnouncement>
          )}
          <PwaInstallPrompt />
        </Container>
      ) : (
        <>
          {window.location.pathname !== "/register" && (
            <DivLang id="div-lang">
              <SelectLang isLogin />
            </DivLang>
          )}
          {window.location.pathname === "/maintenance" ? (
            <Outlet />
          ) : (
            <Main>
              <Outlet />
            </Main>
          )}
          {window.location.pathname !== "/maintenance" && (
            <Aside>
              <RotateBanner
                isLoginPage
                images={[imageLogin1, imageLogin2, imageLogin3]}
                showProgress={false}
                interval={3000}
              />
            </Aside>
          )}
          {showBanner && (
            <PrivacyBanner id={id} onConfirm={() => setShowBanner(false)} />
          )}

          {/*modalRecoverAccount && window.location.pathname === '/login' && (
              <div className="modal">
                <ModalRecoverAccount
                  onCancel={() => setModalRecoverAccount(false)}
                  id={id} />
              </div>
            )*/}
        </>
      )}
    </HeaderProvider>
  );
};

export default Layout;
